import { useState, useEffect, useRef } from "react";
import "./Assginment.css";

/* ---------------- Icons ---------------- */

const Icon = ({ path, size = 16, strokeWidth = 2, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {path}
  </svg>
);

const Clock = (props) => (
  <Icon
    {...props}
    path={
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    }
  />
);
const User = (props) => (
  <Icon
    {...props}
    path={
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c1.2-3.5 4-5 7-5s5.8 1.5 7 5" />
      </>
    }
  />
);
const Flag = (props) => (
  <Icon
    {...props}
    path={
      <>
        <path d="M6 3v18" />
        <path d="M6 4h11l-2.5 3.5L17 11H6" />
      </>
    }
  />
);
const ChevronLeft = (props) => (
  <Icon {...props} path={<path d="M15 6l-6 6 6 6" />} />
);
const ChevronRight = (props) => (
  <Icon {...props} path={<path d="M9 6l6 6-6 6" />} />
);
const Check = (props) => (
  <Icon {...props} path={<path d="M20 6L9 17l-5-5" />} />
);
const AlertCircle = (props) => (
  <Icon
    {...props}
    path={
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </>
    }
  />
);
const FileText = (props) => (
  <Icon
    {...props}
    path={
      <>
        <rect x="5" y="3" width="14" height="18" rx="1.5" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </>
    }
  />
);

/* ---------------- Content ---------------- */

const SECTION = "General Aptitude";
const QUESTIONS = [
  {
    id: 1,
    type: "mcq",
    prompt:
      "A shop offers a 20% discount on a jacket priced at $85. What is the final price?",
    options: ["$65.00", "$68.00", "$70.00", "$72.25"],
  },
  {
    id: 2,
    type: "mcq",
    prompt:
      "Which of the following best describes the relationship between the words 'Author' and 'Book'?",
    options: [
      "Creator to creation",
      "Part to whole",
      "Tool to user",
      "Category to member",
    ],
  },
  {
    id: 3,
    type: "subjective",
    prompt:
      "Describe a time you had to learn a new tool or process quickly. What steps did you take?",
    minWords: 40,
  },
  {
    id: 4,
    type: "mcq",
    prompt:
      "If all Zoraks are Blips, and some Blips are Fenn, which statement must be true?",
    options: [
      "All Zoraks are Fenn",
      "Some Zoraks may be Fenn",
      "No Zoraks are Fenn",
      "All Fenn are Zoraks",
    ],
  },
  {
    id: 5,
    type: "mcq",
    prompt: "Which number comes next in the sequence: 2, 6, 12, 20, 30, ...?",
    options: ["36", "40", "42", "44"],
  },
  {
    id: 6,
    type: "subjective",
    prompt:
      "Explain, in your own words, what a reasonable approach would be to prioritizing three urgent tasks due the same day.",
    minWords: 40,
  },
  {
    id: 7,
    type: "mcq",
    prompt:
      "A train travels 180 km in 3 hours. At the same speed, how long will it take to travel 300 km?",
    options: ["4 hours", "4.5 hours", "5 hours", "5.5 hours"],
  },
  {
    id: 8,
    type: "mcq",
    prompt: "Which word is the odd one out?",
    options: ["Trumpet", "Violin", "Flute", "Clarinet"],
  },
];

const TOTAL_SECONDS = 20 * 60;

function formatTime(s) {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const sec = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${sec}`;
}

function wordCount(text) {
  const t = text.trim();
  return t.length === 0 ? 0 : t.split(/\s+/).length;
}

/* ---------------- Main component ---------------- */

export default function PreAssessment() {
  const [stage, setStage] = useState("intro"); // intro | test | confirm | done
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [visited, setVisited] = useState({ 0: true });
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const timerRef = useRef(null);
  useEffect(() => {
    if (stage !== "test") return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setStage("done");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  const q = QUESTIONS[current];
  const answeredCount = Object.keys(answers).filter(
    (k) => answers[k] && answers[k].length > 0,
  ).length;
  const markedCount = Object.keys(marked).filter((k) => marked[k]).length;
  const notAnsweredCount = QUESTIONS.length - answeredCount;
  const lowTime = secondsLeft <= 120;

  const goTo = (idx) => {
    setVisited((v) => ({ ...v, [idx]: true }));
    setCurrent(idx);
  };

  const setAnswer = (value) => {
    setAnswers((a) => ({ ...a, [current]: value }));
  };

  const clearResponse = () => {
    setAnswers((a) => {
      const next = { ...a };
      delete next[current];
      return next;
    });
  };

  const toggleMark = () => {
    setMarked((m) => ({ ...m, [current]: !m[current] }));
  };

  const next = () => {
    if (current < QUESTIONS.length - 1) goTo(current + 1);
  };

  const prev = () => {
    if (current > 0) goTo(current - 1);
  };

  const statusFor = (idx) => {
    const isAnswered = !!(answers[idx] && answers[idx].length > 0);
    const isMarked = !!marked[idx];
    if (isMarked) return "marked";
    if (isAnswered) return "answered";
    if (visited[idx]) return "not-answered";
    return "not-visited";
  };

  /* ---------------- Intro screen ---------------- */

  if (stage === "intro") {
    return (
      <div className="pa">
        <div className="pa-intro">
          <div className="pa-intro-eyebrow">Pre-Assessment</div>
          <h1 className="pa-intro-title">Baseline Knowledge Assessment</h1>
          <p className="pa-intro-subtitle">
            This short online test establishes a starting point for your
            learning path. Answer as best you can — there's no penalty for an
            honest attempt.
          </p>

          <div className="pa-intro-grid">
            <div className="pa-intro-stat">
              <span className="pa-intro-stat-value">{QUESTIONS.length}</span>
              <span className="pa-intro-stat-label">Questions</span>
            </div>
            <div className="pa-intro-stat">
              <span className="pa-intro-stat-value">20</span>
              <span className="pa-intro-stat-label">Minutes</span>
            </div>
            <div className="pa-intro-stat">
              <span className="pa-intro-stat-value">2</span>
              <span className="pa-intro-stat-label">Question types</span>
            </div>
          </div>

          <div className="pa-intro-rules">
            <div className="pa-intro-rule">
              <span className="pa-intro-rule-dot pa-dot-mcq" />
              Multiple choice — select one option per question.
            </div>
            <div className="pa-intro-rule">
              <span className="pa-intro-rule-dot pa-dot-subjective" />
              Subjective — short written response, aim for{" "}
              {QUESTIONS.find((q) => q.type === "subjective")?.minWords}+ words.
            </div>
            <div className="pa-intro-rule">
              <span className="pa-intro-rule-dot pa-dot-flag" />
              Use "Mark for review" to flag questions and revisit them later.
            </div>
          </div>
          <button
            className="pa-btn pa-btn-primary pa-intro-start"
            onClick={() => setStage("test")}
          >
            Start assessment
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  /* ---------------- Done screen ---------------- */

  if (stage === "done") {
    return (
      <div className="pa">
        <div className="pa-done">
          <div className="pa-done-icon">
            <Check size={28} />
          </div>
          <h1 className="pa-done-title">Assessment submitted</h1>
          <p className="pa-done-subtitle">
            Thanks — your responses are recorded. Subjective answers are
            reviewed individually, so results will follow separately.
          </p>
          <div className="pa-done-summary">
            <div className="pa-done-row">
              <span>Answered</span>
              <span className="pa-done-value pa-text-answered">
                {answeredCount} / {QUESTIONS.length}
              </span>
            </div>
            <div className="pa-done-row">
              <span>Marked for review</span>
              <span className="pa-done-value pa-text-marked">
                {markedCount}
              </span>
            </div>
            <div className="pa-done-row">
              <span>Not answered</span>
              <span className="pa-done-value pa-text-danger">
                {notAnsweredCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- Confirm submit modal ---------------- */

  const ConfirmModal = () => (
    <div className="pa-modal-backdrop">
      <div className="pa-modal">
        <h2 className="pa-modal-title">Submit assessment?</h2>
        <p className="pa-modal-subtitle">
          You can't change your answers after submitting.
        </p>
        <div className="pa-modal-stats">
          <div className="pa-modal-stat">
            <span className="pa-modal-stat-value pa-text-answered">
              {answeredCount}
            </span>
            <span className="pa-modal-stat-label">Answered</span>
          </div>
          <div className="pa-modal-stat">
            <span className="pa-modal-stat-value pa-text-marked">
              {markedCount}
            </span>
            <span className="pa-modal-stat-label">Marked</span>
          </div>
          <div className="pa-modal-stat">
            <span className="pa-modal-stat-value pa-text-danger">
              {notAnsweredCount}
            </span>
            <span className="pa-modal-stat-label">Not answered</span>
          </div>
        </div>
        <div className="pa-modal-actions">
          <button
            className="pa-btn pa-btn-ghost"
            onClick={() => setStage("test")}
          >
            Keep working
          </button>
          <button
            className="pa-btn pa-btn-primary"
            onClick={() => setStage("done")}
          >
            Submit now
          </button>
        </div>
      </div>
    </div>
  );

  /* ---------------- Test screen ---------------- */

  return (
    <div className="pa">
      {stage === "confirm" && <ConfirmModal />}

      <div className="pa-test">
        {/* Top bar */}
        <div className="pa-topbar">
          <div className="pa-topbar-left">
            <FileText size={18} />
            <div>
              <div className="pa-topbar-title">
                Baseline Knowledge Assessment
              </div>
              <div className="pa-topbar-section">{SECTION}</div>
            </div>
          </div>
          <div className={`pa-timer${lowTime ? " pa-timer--low" : ""}`}>
            <Clock size={15} />
            {formatTime(secondsLeft)}
          </div>
        </div>

        <div className="pa-body">
          {/* Main question panel */}
          <div className="pa-panel">
            <div className="pa-panel-header">
              <span className="pa-q-index">
                Question {current + 1} of {QUESTIONS.length}
              </span>
              <span className={`pa-q-type pa-q-type--${q.type}`}>
                {q.type === "mcq" ? "Multiple choice" : "Subjective"}
              </span>
            </div>

            <p className="pa-q-prompt">{q.prompt}</p>

            {q.type === "mcq" ? (
              <div className="pa-options">
                {q.options.map((opt, i) => {
                  const selected = answers[current] === opt;
                  return (
                    <button
                      key={opt}
                      className={`pa-option${selected ? " pa-option--selected" : ""}`}
                      onClick={() => setAnswer(opt)}
                    >
                      <span className="pa-option-marker">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span>{opt}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="pa-subjective">
                <textarea
                  className="pa-textarea"
                  placeholder="Write your response here…"
                  value={answers[current] || ""}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={8}
                />
                <div className="pa-word-count">
                  {wordCount(answers[current] || "")} words
                  {q.minWords ? ` · aim for ${q.minWords}+` : ""}
                </div>
              </div>
            )}

            <div className="pa-panel-footer">
              <button
                className="pa-btn pa-btn-ghost"
                onClick={prev}
                disabled={current === 0}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <div className="pa-panel-footer-right">
                <button className="pa-btn pa-btn-ghost" onClick={clearResponse}>
                  Clear response
                </button>
                <button
                  className={`pa-btn pa-btn-flag${marked[current] ? " pa-btn-flag--active" : ""}`}
                  onClick={toggleMark}
                >
                  <Flag size={15} />
                  {marked[current] ? "Marked" : "Mark for review"}
                </button>
                {current === QUESTIONS.length - 1 ? (
                  <button
                    className="pa-btn pa-btn-primary"
                    onClick={() => setStage("confirm")}
                  >
                    Submit test
                  </button>
                ) : (
                  <button className="pa-btn pa-btn-primary" onClick={next}>
                    Save &amp; next
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="pa-sidebar">
            <div className="pa-candidate">
              <div className="pa-candidate-avatar">
                <User size={16} />
              </div>
              <div>
                <div className="pa-candidate-name">Candidate</div>
                <div className="pa-candidate-meta">
                  Baseline test · Attempt 1
                </div>
              </div>
            </div>

            <div className="pa-sidebar-card">
              <div className="pa-sidebar-card-title">Question palette</div>
              <div className="pa-palette">
                {QUESTIONS.map((qq, i) => (
                  <button
                    key={qq.id}
                    className={`pa-palette-item pa-palette-item--${statusFor(i)}${
                      i === current ? " pa-palette-item--current" : ""
                    }`}
                    onClick={() => goTo(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <div className="pa-legend">
                <div className="pa-legend-item">
                  <span className="pa-legend-dot pa-palette-item--answered" />
                  Answered
                </div>
                <div className="pa-legend-item">
                  <span className="pa-legend-dot pa-palette-item--not-answered" />
                  Not answered
                </div>
                <div className="pa-legend-item">
                  <span className="pa-legend-dot pa-palette-item--marked" />
                  Marked for review
                </div>
                <div className="pa-legend-item">
                  <span className="pa-legend-dot pa-palette-item--not-visited" />
                  Not visited
                </div>
              </div>
            </div>

            <div className="pa-sidebar-card pa-sidebar-summary">
              <div className="pa-summary-row">
                <span className="pa-text-answered">
                  <Check size={13} /> Answered
                </span>
                <span>{answeredCount}</span>
              </div>
              <div className="pa-summary-row">
                <span className="pa-text-danger">
                  <AlertCircle size={13} /> Not answered
                </span>
                <span>{notAnsweredCount}</span>
              </div>
              <div className="pa-summary-row">
                <span className="pa-text-marked">
                  <Flag size={13} /> Marked
                </span>
                <span>{markedCount}</span>
              </div>
            </div>

            <button
              className="pa-btn pa-btn-primary pa-submit-btn"
              onClick={() => setStage("confirm")}
            >
              Submit test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
